import { normalizeMackerelDaySelection } from "./mackerelPresentation";
import { normalizeMackerelCapabilities } from "./mackerelCapabilities";
import API_BASE, { protectedApiFetch } from "../../api";
import { normalizeModeledPeriod, SEA_BASS_RANKING_POLICY, MACKEREL_RANKING_POLICY } from "./seaBassPresentation";
import {
  FISHING_TODAY_LOCATION_SOURCE,
  isCoordinateFishingLocation,
  roundFishingCoordinate,
} from "../fishingToday/fishingTodayLocation";
import {
  COMMON_CARP_SPECIES_ID,
  isSupportedFishingIntelligenceSpecies,
} from "./speciesCatalog";
import {
  ACCESS_CONTEXTS,
  HABITAT_FEATURES,
  normalizeFishingSetting,
} from "./fishingSetting";

const SCORE_LABELS = new Set([
  "very_poor",
  "poor",
  "fair",
  "good",
  "very_good",
  "excellent",
]);
const CONFIDENCE_LEVELS = new Set(["low", "moderate", "high"]);
const DECLARED_HABITAT_SOURCES = new Set(["user_confirmed", "unknown"]);
const FORECAST_COVERAGE_STATUSES = new Set(["complete", "partial"]);
const FRESHWATER_TEMPERATURE_STATUSES = new Set([
  "trusted_current",
  "trusted_timeline",
  "found_untrusted",
  "unavailable",
  "not_applicable",
]);
const SUPPORTED_SCHEMA_VERSIONS = new Set(["1.0", "1.1", "1.2"]);
const accessContexts = new Set(ACCESS_CONTEXTS);
const habitatFeatures = new Set(HABITAT_FEATURES);

export class FishingIntelligenceApiError extends Error {
  constructor(
    message,
    { code = "fishing_intelligence_unavailable", status = 0, category = "network" } = {}
  ) {
    super(message);
    this.name = "FishingIntelligenceApiError";
    this.code = code;
    this.status = status;
    this.category = category;
  }
}

const finiteScore = (value) =>
  Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;

const normalizeScore = (value) => {
  const score = finiteScore(value?.score);
  if (score === null || !SCORE_LABELS.has(value?.label)) return null;
  const rawScore = finiteScore(value?.raw_score);
  return { score, label: value.label, ...(rawScore === null ? {} : { rawScore }) };
};

const normalizeConfidenceComponents = (value) =>
  Array.isArray(value)
    ? value
        .map((item) => ({
          id: typeof item?.id === "string" ? item.id : null,
          quality: finiteScore(item?.quality),
        }))
        .filter((item) => item.id && item.quality !== null)
    : [];

const normalizeConfidence = (value) => {
  const score = finiteScore(value?.score);
  if (score === null || !CONFIDENCE_LEVELS.has(value?.level)) return null;
  return {
    score,
    level: value.level,
    components: normalizeConfidenceComponents(value?.components),
  };
};

const stringArray = (value) =>
  Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];

const normalizeReason = (value = {}) => ({
  code: typeof value.code === "string" ? value.code : null,
  dimension: typeof value.dimension === "string" ? value.dimension : null,
  direction: ["positive", "neutral", "negative"].includes(value.direction)
    ? value.direction
    : null,
  strength: Number.isFinite(value.strength) ? value.strength : null,
  sourceLayer:
    typeof value.source_layer === "string" ? value.source_layer : null,
  caveat: value.caveat === true,
});

const normalizeReasons = (value) =>
  Array.isArray(value)
    ? value.map(normalizeReason).filter((item) => item.code)
    : [];

const normalizeDeclaredHabitat = (value = {}) => ({
  habitatFeatures: Array.from(
    new Set(stringArray(value.habitat_features).filter((item) => habitatFeatures.has(item)))
  ).sort(),
  accessContext: accessContexts.has(value.access_context) ? value.access_context : "unknown",
  source: DECLARED_HABITAT_SOURCES.has(value.source) ? value.source : "unknown",
  scoringActive: value.scoring_active === true,
});

const normalizePredictionPoint = (value = {}) => ({
  timestamp: typeof value.timestamp === "string" ? value.timestamp : null,
  fishingOpportunity: normalizeScore(value.fishing_opportunity),
  availability: normalizeScore(value.availability),
  activity: normalizeScore(value.activity),
  fishability: normalizeScore(value.fishability),
  methodSuitability: normalizeScore(value.method_suitability),
  confidence: normalizeConfidence(value.confidence),
  safety: {
    status: typeof value.safety?.status === "string" ? value.safety.status : "not_assessed",
    reasonCodes: stringArray(value.safety?.reason_codes),
  },
  eligibility: {
    biological:
      typeof value.eligibility?.biological === "string"
        ? value.eligibility.biological
        : "uncertain",
    physicalAccess:
      typeof value.eligibility?.physical_access === "string"
        ? value.eligibility.physical_access
        : "uncertain",
    recommendedWindow: value.eligibility?.recommended_window === true,
    reasonCodes: stringArray(value.eligibility?.reason_codes),
  },
  missingInputs: stringArray(value.missing_inputs),
  reasons: normalizeReasons(value.reasons),
});

const localDate = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;

const normalizeForecastMetadata = (value = {}) => ({
  requestedDays: value.requested_days === 3 ? 3 : null,
  availableLocalDates: Array.isArray(value.available_local_dates)
    ? value.available_local_dates.map(localDate).filter(Boolean)
    : [],
  timezone: typeof value.timezone === "string" ? value.timezone : null,
  horizonStatus: FORECAST_COVERAGE_STATUSES.has(value.horizon_status)
    ? value.horizon_status
    : null,
  pointCount:
    Number.isInteger(value.point_count) && value.point_count >= 0
      ? value.point_count
      : null,
});

const normalizeForecastDay = (value = {}, speciesId) => ({
  lightCycle: value.light_cycle?.source === "local_noaa_meeus" &&
    ["ordinary", "polar_day", "polar_night"].includes(value.light_cycle.state)
    ? {
        source: value.light_cycle.source,
        state: value.light_cycle.state,
        sunrise: typeof value.light_cycle.sunrise === "string" ? value.light_cycle.sunrise : null,
        sunset: typeof value.light_cycle.sunset === "string" ? value.light_cycle.sunset : null,
      }
    : null,
  ...(value.ranking_policy === SEA_BASS_RANKING_POLICY ? {
    rankingPolicy: SEA_BASS_RANKING_POLICY,
    modeledPeriod: normalizeModeledPeriod(value.modeled_period, normalizeScore, normalizeConfidence),
    isBestModeledPeriod: value.is_best_modeled_period === true,
    dayComparison: value.day_comparison,
  } : {}),
  ...(speciesId === "scomber_scombrus" && value.ranking_policy === MACKEREL_RANKING_POLICY
    ? normalizeMackerelDaySelection(value, normalizeScore, normalizeConfidence) : {}),
  localDate: localDate(value.local_date),
  coverageStatus: FORECAST_COVERAGE_STATUSES.has(value.coverage_status)
    ? value.coverage_status
    : null,
  availableHourCount:
    Number.isInteger(value.available_hour_count) && value.available_hour_count >= 0
      ? value.available_hour_count
      : null,
  points: Array.isArray(value.timeline)
    ? value.timeline.map(normalizePredictionPoint)
    : [],
});

export const normalizeFishingIntelligenceOutlook = (payload = {}) => {
  const source = payload && typeof payload === "object" ? payload : {};
  const schemaVersion =
    typeof source.schema_version === "string" ? source.schema_version : null;
  const timelinePoints = Array.isArray(source.timeline?.points)
    ? source.timeline.points.map(normalizePredictionPoint)
    : [];
  const supportsMultiDay = schemaVersion === "1.2";

  return {
    schemaVersion,
    next24Hours: source.next_24_hours?.ranking_policy === "raw_modeled_period_1" ? {
      referenceTime: source.next_24_hours.reference_time,
      start: source.next_24_hours.start,
      end: source.next_24_hours.end,
      rankingPolicy: "raw_modeled_period_1",
      coverageStatus: source.next_24_hours.coverage_status,
      availableHourCount: source.next_24_hours.available_hour_count,
      points: Array.isArray(source.next_24_hours.timeline) ? source.next_24_hours.timeline.map(normalizePredictionPoint) : [],
      modeledPeriod: normalizeModeledPeriod(source.next_24_hours.modeled_period, normalizeScore, normalizeConfidence),
    } : null,
    species: {
      id: typeof source.species?.id === "string" ? source.species.id : null,
      scientificName:
        typeof source.species?.scientific_name === "string"
          ? source.species.scientific_name
          : null,
      profileMaturity:
        typeof source.species?.profile_maturity === "string"
          ? source.species.profile_maturity
          : null,
    },
    context: {
      ...(source.species?.id === "scomber_scombrus" ? {
        capabilities: normalizeMackerelCapabilities(source.species.id, source.capabilities),
      } : {}),
      ...(source.context?.fishing_context_status ? {
        fishingContextStatus: source.context.fishing_context_status,
        fishingPlatform: source.context.fishing_context?.platform || null,
      } : {}),
      ...(source.species?.id === "dicentrarchus_labrax" && source.versions?.profile?.startsWith("sea_bass_multifactor_1:") ? { seaBassMultifactor: true } : {}),
      regionId: typeof source.context?.region?.id === "string" ? source.context.region.id : null,
      supportedForSpecies: source.context?.region?.supported_for_species,
      isTransition: source.context?.region?.is_transition === true,
      resolutionQuality:
        typeof source.context?.region?.resolution_quality === "string"
          ? source.context.region.resolution_quality
          : null,
      resolutionLevel:
        typeof source.context?.region?.resolution_level === "string"
          ? source.context.region.resolution_level
          : null,
      waterbody:
        typeof source.context?.waterbody === "string" ? source.context.waterbody : "unknown",
      locationPrecision:
        typeof source.context?.location_precision === "string"
          ? source.context.location_precision
          : "unknown",
      habitatKnown: source.context?.habitat_known === true,
      waterbodyIdentified: source.context?.waterbody_identified === true,
      freshwaterTemperature: {
        status: FRESHWATER_TEMPERATURE_STATUSES.has(
          source.context?.freshwater_temperature?.status
        )
          ? source.context.freshwater_temperature.status
          : "not_applicable",
        source: ["automatic", "user_measured"].includes(
          source.context?.freshwater_temperature?.source
        )
          ? source.context.freshwater_temperature.source
          : null,
        temperatureC: Number.isFinite(
          source.context?.freshwater_temperature?.temperature_c
        )
          ? source.context.freshwater_temperature.temperature_c
          : null,
        observedAt:
          typeof source.context?.freshwater_temperature?.observed_at === "string"
            ? source.context.freshwater_temperature.observed_at
            : null,
        futureSupported:
          source.context?.freshwater_temperature?.future_supported === true,
      },
      declaredHabitat: normalizeDeclaredHabitat(source.context?.declared_habitat),
      forecastTimezone:
        typeof source.context?.forecast_timezone === "string"
          ? source.context.forecast_timezone
          : null,
    },
    current: normalizePredictionPoint(source.current),
    timeline: {
      ...(source.timeline?.ranking_policy === SEA_BASS_RANKING_POLICY ? {
        rankingPolicy: SEA_BASS_RANKING_POLICY,
        modeledPeriod: normalizeModeledPeriod(source.timeline.modeled_period, normalizeScore, normalizeConfidence),
      } : {}),
      start: typeof source.timeline?.timeline_start === "string" ? source.timeline.timeline_start : null,
      end: typeof source.timeline?.timeline_end === "string" ? source.timeline.timeline_end : null,
      points: timelinePoints,
    },
    forecastMetadata:
      supportsMultiDay && source.forecast_metadata
        ? normalizeForecastMetadata(source.forecast_metadata)
        : null,
    forecastDays:
      supportsMultiDay && Array.isArray(source.forecast_days)
        ? source.forecast_days.map(day => normalizeForecastDay(day, source.species?.id)).slice(0, 3)
        : [],
    bestWindows: Array.isArray(source.best_windows) ? source.best_windows : [],
    missingInputs: stringArray(source.missing_inputs),
    reasons: normalizeReasons(source.reasons),
  };
};

const readPayload = async (response) => {
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
};

const errorCategoryFor = (status) => {
  if (status === 401 || status === 403) return "authentication";
  if (status >= 400 && status < 500) return "request";
  return "service";
};

export async function fetchFishingIntelligenceOutlook({
  speciesId,
  fishingPlatform = null,
  location,
  declaredHabitat,
  sessionEvidence,
  signal,
}) {
  if (!isSupportedFishingIntelligenceSpecies(speciesId)) {
    throw new TypeError("A supported species identifier is required.");
  }

  const body = { species_id: speciesId };
  if (sessionEvidence && speciesId !== COMMON_CARP_SPECIES_ID) {
    throw new TypeError("Session Carp evidence can only be sent for Common Carp.");
  }
  if (speciesId === COMMON_CARP_SPECIES_ID && sessionEvidence) {
    if (sessionEvidence.carpPresent === true) {
      body.session_carp_present = true;
    }
    if (sessionEvidence.measuredFreshwaterTemperatureC != null) {
      if (!Number.isFinite(sessionEvidence.measuredFreshwaterTemperatureC)) {
        throw new TypeError("Measured freshwater temperature must be finite.");
      }
      body.session_measured_freshwater_temperature_c =
        sessionEvidence.measuredFreshwaterTemperatureC;
    }
  }
  if (
    location?.source === FISHING_TODAY_LOCATION_SOURCE.LIVE ||
    location?.source === FISHING_TODAY_LOCATION_SOURCE.FAVORITE_SPOT
  ) {
    if (!isCoordinateFishingLocation(location)) {
      throw new TypeError("Precise location requires finite latitude and longitude.");
    }
    body.lat = roundFishingCoordinate(location.latitude);
    body.lng = roundFishingCoordinate(location.longitude);
    body.location_source = location.source;
  }
  const normalizedSetting = normalizeFishingSetting(declaredHabitat);
  if (normalizedSetting.habitatFeatures.length > 0) {
    body.habitat_features = normalizedSetting.habitatFeatures;
  }
  if (normalizedSetting.accessContext !== null) {
    body.access_context = normalizedSetting.accessContext;
  }
  if (speciesId === "dicentrarchus_labrax") {
    if (fishingPlatform !== null && !["shore", "boat_kayak"].includes(fishingPlatform)) {
      throw new TypeError("Explicit marine platform is invalid.");
    }
    body.fishing_context = {
      platform: fishingPlatform,
      habitat_features: normalizedSetting.habitatFeatures,
      access_context: normalizedSetting.accessContext || "unknown",
    };
    delete body.habitat_features;
    delete body.access_context;
  }

  let response;
  try {
    response = await protectedApiFetch(`${API_BASE}/fishing-intelligence/outlook`, {
      method: "POST",
      cache: "no-store",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    throw new FishingIntelligenceApiError("Species outlook request failed.", {
      code: "fishing_intelligence_network_error",
      category: "network",
    });
  }

  const payload = await readPayload(response);
  if (!response.ok) {
    const detail = payload?.detail;
    const code =
      (typeof detail === "object" && typeof detail.code === "string" && detail.code) ||
      "fishing_intelligence_unavailable";
    throw new FishingIntelligenceApiError("Species outlook is temporarily unavailable.", {
      code,
      status: response.status,
      category: errorCategoryFor(response.status),
    });
  }

  if (
    !SUPPORTED_SCHEMA_VERSIONS.has(payload?.schema_version) ||
    payload?.species?.id !== speciesId ||
    !payload?.context ||
    !payload?.current
  ) {
    throw new FishingIntelligenceApiError("Species outlook response was invalid.", {
      code: "fishing_intelligence_invalid_response",
      status: response.status,
      category: "service",
    });
  }

  return normalizeFishingIntelligenceOutlook(payload);
}
